// frontend/src/components/dialogs/StartConfirmDialog.js
import React, { useEffect, useMemo, useState } from 'react';
import { LucideSave, BookX, Info, AlertTriangle } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { useTheme } from '../../contexts/ThemeContext';
import ConfirmDialogShell from './ConfirmDialogShell';

// Note: No fallback types - only show what's actually available in the database
const getRouteTypeLabel = (type, t) => {
    // If type is an object (new format with custom types), use its properties
    if (typeof type === 'object' && type !== null) {
        // For system types (representative, alternative, seasonal), use frontend i18n translations
        // This ensures they show in the user's selected language even if database translations aren't set
        if (type.type_key === 'representative') {
            return {
                title: t('routeType.representative.title'),
                description: t('routeType.representative.description'),
                value: type.type_key
            };
        } else if (type.type_key === 'alternative') {
            return {
                title: t('routeType.alternative.title'),
                description: t('routeType.alternative.description'),
                value: type.type_key
            };
        } else if (type.type_key === 'seasonal') {
            return {
                title: t('routeType.seasonal.title'),
                description: t('routeType.seasonal.description'),
                value: type.type_key
            };
        }

        // For custom types, use the translated display_name from backend
        return {
            title: `${type.icon || ''} ${type.display_name || type.type_key}`.trim(),
            description: type.description || '',
            value: type.type_key
        };
    }

    // Legacy support: type is a string (old format)
    const typeStr = String(type);
    switch (typeStr) {
        case 'alternative':
            return {
                title: t('routeType.alternative.title'),
                description: t('routeType.alternative.description'),
                value: typeStr
            };
        case 'seasonal':
            return {
                title: t('routeType.seasonal.title'),
                description: t('routeType.seasonal.description'),
                value: typeStr
            };
        case 'representative':
        default:
            return {
                title: t('routeType.representative.title'),
                description: t('routeType.representative.description'),
                value: typeStr
            };
    }
};

export const StartConfirmDialog = ({
    isOpen,
    onClose,
    onConfirm,
    availableTypes = [],
    defaultType = null,
    vehicleOptions = [],
    selectedVehicleId = null,
    onVehicleChange,
    vehicleConflict = null,
    requiresVehicleSelection = false,
    gpsReadinessRequired = false,
    gpsReadinessReady = true,
    gpsReadinessMessageKey = null
}) => {
    const { t } = useTranslation();
    const { cn, text, background, border } = useTheme();
    const [selectedType, setSelectedType] = useState(defaultType);

    const typeOptions = useMemo(() => {
        // Only show types that are actually available in the database
        const source = Array.isArray(availableTypes) ? availableTypes : [];
        return source.map((typeData) => {
            const label = getRouteTypeLabel(typeData, t);
            return {
                value: label.value || (typeof typeData === 'string' ? typeData : typeData.type_key),
                title: label.title,
                description: label.description
            };
        });
    }, [availableTypes, t]);

    // Determine if we need route type selection (2+ types) or simple confirmation (0-1 types)
    const showRouteTypeSelection = typeOptions.length > 1;
    const autoResolvedType = typeOptions.length === 1 ? typeOptions[0].value : null;

    useEffect(() => {
        if (showRouteTypeSelection) {
            // Multiple types available - use defaultType or first option
            const normalizedDefault = defaultType && typeOptions.find(option => option.value === defaultType)
                ? defaultType
                : typeOptions[0]?.value || null;
            setSelectedType(normalizedDefault);
        } else {
            // 0 or 1 types - auto-resolve
            setSelectedType(autoResolvedType || defaultType || null);
        }
    }, [defaultType, typeOptions, showRouteTypeSelection, autoResolvedType, isOpen]);

    const handleConfirm = () => {
        const options = {};
        if (selectedVehicleId) {
            options.vehicleId = selectedVehicleId;
        }
        if (vehicleConflict) {
            options.overrideVehicleConflict = true;
        }

        // Pass the resolved type to parent (can be null if no reference data exists)
        onConfirm(selectedType, options);
    };

    const hasVehicleOptions = Array.isArray(vehicleOptions) && vehicleOptions.length > 0;
    const mustSelectVehicle = requiresVehicleSelection || hasVehicleOptions;
    const noActiveVehiclesAvailable = mustSelectVehicle && !hasVehicleOptions;
    const gpsReadinessBlocksStart = gpsReadinessRequired && !gpsReadinessReady;
    const confirmDisabled = noActiveVehiclesAvailable
        || (mustSelectVehicle && !selectedVehicleId)
        || gpsReadinessBlocksStart;

    return (
        <ConfirmDialogShell
            isOpen={isOpen}
            onClose={onClose}
            onConfirm={handleConfirm}
            title={t('startTrip')}
            confirmIcon={LucideSave}
            cancelIcon={BookX}
            confirmVariant={vehicleConflict ? 'warning' : 'primary'}
            confirmLabel={vehicleConflict ? t('vehicleConflict.confirmOverride') : undefined}
            isDisabled={confirmDisabled}
        >
            <div className="space-y-4">
                {vehicleConflict && (
                    <div
                        className={cn(
                            'flex items-start gap-2 p-4 rounded-lg',
                            'bg-amber-50 dark:bg-amber-900/20 border border-amber-200 dark:border-amber-700'
                        )}
                    >
                        <AlertTriangle className="h-5 w-5 text-amber-500 mt-0.5 flex-shrink-0" />
                        <div className="space-y-2">
                            <p className={cn('text-sm font-semibold', text.primary)}>
                                {t('vehicleConflict.title')}
                            </p>
                            <p className={cn('text-sm', text.secondary)}>
                                {t('vehicleConflict.message')}
                            </p>
                            {vehicleConflict.blockingTrips?.map((trip) => (
                                <p key={trip.tripId} className={cn('text-xs', text.secondary)}>
                                    {t('vehicleConflict.blockingTrip', {
                                        tripId: trip.tripId,
                                        vehicleName: trip.vehicleName || trip.vehicleId,
                                        routeName: trip.routeName || ''
                                    })}
                                </p>
                            ))}
                        </div>
                    </div>
                )}
                {noActiveVehiclesAvailable && (
                    <div
                        className={cn(
                            'flex items-start gap-2 p-4 rounded-lg',
                            'bg-amber-50 dark:bg-amber-900/20 border border-amber-200 dark:border-amber-700'
                        )}
                    >
                        <AlertTriangle className="h-5 w-5 text-amber-500 mt-0.5 flex-shrink-0" />
                        <p className={cn('text-sm', text.secondary)}>
                            {t('vehicleConflict.noActiveVehicles')}
                        </p>
                    </div>
                )}
                {hasVehicleOptions && (
                    <label className="block space-y-2">
                        <span className={cn('text-sm font-medium', text.primary)}>
                            {t('vehicleConflict.selectVehicle')}
                        </span>
                        <select
                            value={selectedVehicleId || ''}
                            onChange={(event) => onVehicleChange?.(event.target.value ? Number(event.target.value) : null)}
                            className={cn('w-full px-3 py-2 rounded border', border.default, background.secondary, text.primary)}
                        >
                            <option value="">{t('vehicleConflict.noVehicleSelected')}</option>
                            {vehicleOptions.map((vehicle) => (
                                <option key={vehicle.id} value={vehicle.id}>
                                    {vehicle.vehicle_number || vehicle.name || vehicle.id}
                                </option>
                            ))}
                        </select>
                    </label>
                )}
                {gpsReadinessBlocksStart && (
                    <div
                        className={cn(
                            'flex items-start gap-2 p-4 rounded-lg',
                            'bg-amber-50 dark:bg-amber-900/20 border border-amber-200 dark:border-amber-700'
                        )}
                    >
                        <AlertTriangle className="h-5 w-5 text-amber-500 mt-0.5 flex-shrink-0" />
                        <p className={cn('text-sm', text.secondary)}>
                            {t(gpsReadinessMessageKey || 'gpsSource.startReadiness.waitingForFeed')}
                        </p>
                    </div>
                )}
                {showRouteTypeSelection ? (
                    // Mode 1: Multiple route types available - show selection UI
                    <>
                        <div
                            className={cn(
                                'flex items-start gap-2 p-3 rounded-lg',
                                'bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-700'
                            )}
                        >
                            <Info className="h-5 w-5 text-blue-500 mt-0.5 flex-shrink-0" />
                            <p className={cn('text-sm', text.secondary)}>
                                {t('routeType.selectionPrompt')}
                            </p>
                        </div>
                        <div className="space-y-3">
                            {typeOptions.map((option) => (
                                <label
                                    key={option.value}
                                    className={cn(
                                        'flex gap-3 p-4 rounded-lg border-2 cursor-pointer transition-colors',
                                        selectedType === option.value
                                            ? 'border-blue-500 bg-blue-50 dark:bg-blue-900/30'
                                            : cn(border.default, background.secondary, 'hover:border-blue-300')
                                    )}
                                >
                                    <input
                                        type="radio"
                                        name="routeType"
                                        value={option.value}
                                        checked={selectedType === option.value}
                                        onChange={(event) => setSelectedType(event.target.value)}
                                        className="mt-1 form-radio text-blue-500 focus:ring-blue-500"
                                    />
                                    <div>
                                        <p className={cn('font-semibold', text.primary)}>{option.title}</p>
                                        <p className={cn('text-sm', text.secondary)}>{option.description}</p>
                                    </div>
                                </label>
                            ))}
                        </div>
                        <p className={cn('text-xs text-center', text.tertiary)}>
                            {t('routeType.selectionNote')}
                        </p>
                    </>
                ) : (
                    // Mode 2: 0 or 1 route type - show simple confirmation (no technical details)
                    <div
                        className={cn(
                            'flex items-start gap-2 p-4 rounded-lg',
                            'bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-700'
                        )}
                    >
                        <Info className="h-5 w-5 text-blue-500 mt-0.5 flex-shrink-0" />
                        <div className="flex-1">
                            <p className={cn('text-sm', text.secondary)}>
                                {t('confirmStartTrip')}
                            </p>
                        </div>
                    </div>
                )}
            </div>
        </ConfirmDialogShell>
    );
};

export default StartConfirmDialog;
